import type { OwnedIncidentRef } from '@tsr/shared-types';
import {
  incidentCommandEstablishedLabel,
  unifiedCommandEstablishedLabel,
  LIAISON_OFFICER_LABEL,
  INCIDENT_COMMANDER_LABEL,
  PRIMARY_CONTACT_SUFFIX,
  jurisdictionTierLabel,
  declaredStateLabel,
  TRANSFER_OF_COMMAND_LABEL,
  UNIFIED_COMMAND_INVITE_LABEL,
} from '../incidentVocabulary';
import { ResponseLevelBadge } from '../ResponseLevelBadge';
import { humanizeAgo } from '../FreshnessIndicator';

/**
 * OWNERSHIP COORDINATION BLOCK (feat/ownership-coordination) — the CROSS-AGENCY common-picture read
 * on a hazard's side panel: every COP operator, in ANY agency, sees WHO holds Incident Command and
 * how to reach them, without being able to act on someone else's incident (the command CONTROLS
 * stay in IncidentManagementSection, gated to the owner). Renders from the hazard payload's
 * `owned_incident` ref (the incident_common_picture view), NOT the RLS-gated incident detail — so it
 * is visible to a non-participant neighbour, which the RLS-gated command fetch is not.
 *
 * COMMAND is exclusive; the PICTURE is shared. Attribution = Lead Organization + Incident Commander;
 * CONTACT routes through the Liaison Officer (App A #46 — the outside-contact for requests for
 * assistance), FALLING BACK to the Incident Commander when no LO is stood up (§6.2 / §3.2).
 */
function relSince(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return humanizeAgo(Date.now() - t);
}

/** One contact line — role · name · 📞 tel · ✉ mail (scoped disclosure), with an optional
 *  "(primary contact)" annotation on the Liaison Officer duty line. */
function ContactLine({
  role,
  name,
  phone,
  email,
  primary,
  testidPrefix,
}: {
  role: string;
  name: string;
  phone?: string | null;
  email?: string | null;
  primary?: boolean;
  testidPrefix: string;
}): JSX.Element {
  const tel = phone ? `tel:${phone.replace(/[^\d+]/g, '')}` : null;
  return (
    <p className="mt-0.5 text-[11px] text-slate-300">
      <span
        data-testid={`${testidPrefix}-role`}
        className="mr-1 text-[9px] uppercase tracking-wide text-slate-500"
      >
        {role}
      </span>
      <span data-testid={`${testidPrefix}-name`} className="font-medium text-slate-100">
        {name}
      </span>
      {primary && (
        <span className="ml-1 text-[9px] text-emerald-300/90">({PRIMARY_CONTACT_SUFFIX})</span>
      )}
      {tel && (
        <>
          {' · '}
          <a
            data-testid={`${testidPrefix}-tel`}
            href={tel}
            className="text-emerald-300 hover:underline"
            title={`Call ${name} at ${phone}`}
          >
            📞 {phone}
          </a>
        </>
      )}
      {email && (
        <>
          {' · '}
          <a
            data-testid={`${testidPrefix}-mail`}
            href={`mailto:${email}`}
            className="text-emerald-300 hover:underline"
            title={`Email ${name}`}
          >
            ✉ email
          </a>
        </>
      )}
    </p>
  );
}

export function OwnershipCoordinationBlock({
  owned,
}: {
  owned: OwnedIncidentRef;
}): JSX.Element | null {
  const org = owned.lead_organization_name;
  // No live command lead ⇒ nothing to coordinate (the "Owned · incident #N" chip already shows the
  // incident number). Guard so a closed/un-led incident's ref renders nothing here.
  if (!org) return null;

  const coLeadNames = (owned.co_leads ?? []).filter(Boolean).map((c) => c.agency_name);
  const unified = owned.unified === true && coLeadNames.length >= 2;
  // §4.7 — a unified incident MUST read "Unified Command established — <A> · <B>" (single-command
  // copy on a unified incident is a ❌ DOCTRINE-MISMATCH); a single incident reads the single form.
  const heading = unified
    ? unifiedCommandEstablishedLabel(coLeadNames)
    : incidentCommandEstablishedLabel(org);

  const since = relSince(owned.command_assumed_at);
  const viewerIsLead = owned.viewer_is_lead === true;

  // Contact routes through the Liaison Officer when stood up; else the Incident Commander (§3.2).
  const contact = owned.liaison_officer_name
    ? {
        role: LIAISON_OFFICER_LABEL,
        name: owned.liaison_officer_name,
        phone: owned.liaison_officer_phone,
        email: owned.liaison_officer_email,
        primary: true,
      }
    : owned.incident_commander_name
      ? {
          role: INCIDENT_COMMANDER_LABEL,
          name: owned.incident_commander_name,
          phone: owned.incident_commander_phone,
          email: owned.incident_commander_email,
          primary: false,
        }
      : null;

  const pendingKind =
    owned.pending_transfer_kind === 'unified_invite'
      ? UNIFIED_COMMAND_INVITE_LABEL
      : TRANSFER_OF_COMMAND_LABEL;

  return (
    <section
      data-testid="ownership-coordination-block"
      data-viewer-is-lead={viewerIsLead ? 'true' : 'false'}
      className="mt-2 rounded border border-slate-700/70 bg-slate-900/40 p-2"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span
          data-testid="coordination-heading"
          className="text-[11px] font-semibold text-slate-100"
        >
          {heading}
        </span>
        <span
          data-testid="coordination-tier"
          className="rounded bg-slate-700/60 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-slate-300"
        >
          {jurisdictionTierLabel(owned.lead_organization_kind)}
        </span>
        {owned.response_level != null && (
          <ResponseLevelBadge
            level={owned.response_level}
            axis="response"
            testid="coordination-response-level"
          />
        )}
      </div>

      {/* Who holds it (accountability) + since (freshness — the IC rotates every operational period). */}
      {owned.incident_commander_name && (
        <p className="mt-1 text-[11px] text-slate-300">
          <span className="mr-1 text-[9px] uppercase tracking-wide text-slate-500">
            {INCIDENT_COMMANDER_LABEL}
          </span>
          <span data-testid="coordination-ic-name" className="font-medium text-slate-100">
            {owned.incident_commander_name}
          </span>
          {since && <span className="text-slate-400"> · since {since}</span>}
        </p>
      )}

      {/* Unified co-lead roster (§4.7) — one shared command, the full set of Lead Organizations. */}
      {unified && (
        <p data-testid="coordination-coleads" className="mt-0.5 text-[10px] text-slate-400">
          {coLeadNames.join(' · ')}
        </p>
      )}

      {/* CONTACT — the Liaison Officer duty line (primary), or the Incident Commander fallback. */}
      {contact && (
        <ContactLine
          role={contact.role}
          name={contact.name}
          phone={contact.phone}
          email={contact.email}
          primary={contact.primary}
          testidPrefix="coordination-contact"
        />
      )}

      {/* Declared state (statutory — the mayor asks within 60 seconds). */}
      {owned.declared_authority_kind && (
        <p
          data-testid="coordination-declared"
          className="mt-1 text-[10px] font-medium text-amber-300"
        >
          {declaredStateLabel(owned.declared_authority_kind, owned.declaration_number)}
        </p>
      )}

      {/* Pending transfer of command — calling the outgoing IC mid-handover wastes 20 minutes. */}
      {owned.pending_transfer_to && (
        <p
          data-testid="coordination-pending-transfer"
          className="mt-1 rounded bg-amber-950/30 px-1.5 py-0.5 text-[10px] text-amber-300"
        >
          {pendingKind} pending → {owned.pending_transfer_to}
        </p>
      )}
    </section>
  );
}
